import { execAdmin } from '@src/apis/reware/utils/exec';

import type {
  FetchGiftcardsFilters,
  GiftcardConstants,
  GiftcardRecord,
  GiftcardsPage,
  GiftcardsResult,
} from './types';

const STATE_TRANSITIONS = `
  stateTransitions {
    id
    createdAt
    changeType
    changeFrom
    changeTo
    reason
    note
    user { name }
  }
`;

interface FetchGiftcardsResponse {
  giftcards: GiftcardsPage;
}

/**
 * Partner-scoped version of the admin `giftcards` query used by reware-admin's Gift cards portal.
 *
 * The selection is close to the admin portal's but not identical and is not kept in sync: this
 * adds `uuid` and `vendorSource`, trims the state-transition fragment to the columns the activity
 * tabs render, and omits the admin's marketMakerId and createdAt filters.
 *
 * `number` arrives pre-masked as `***` + the last three digits; the full number and the PIN are
 * fetched on demand through revealSensitiveDetail, which the server audits.
 */
export const fetchGiftcards = async (filters: FetchGiftcardsFilters): Promise<GiftcardsResult> => {
  const query = `
    query fetchGiftcards(
      $page: Int
      $pageSize: Int
      $partnerId: Int
      $state: String
      $source: String
      $reason: String
      $giftcardNumber: String
      $giftcardId: Int
      $orderNumber: String
      $tradeinNumber: String
      $amount: Int
      $firstName: String
      $lastName: String
      $email: String
      $phone: String
    ) {
      giftcards(
        page: $page
        pageSize: $pageSize
        partnerId: $partnerId
        state: $state
        source: $source
        reason: $reason
        number: $giftcardNumber
        id: $giftcardId
        orderNumber: $orderNumber
        tradeinNumber: $tradeinNumber
        amount: $amount
        firstName: $firstName
        lastName: $lastName
        email: $email
        phone: $phone
      ) {
        count
        page
        pageSize
        rows {
          id
          number
          uuid
          amount
          state
          partnerId
          createdAt
          canResendGiftcardEmail
          source
          reason
          balanceAmount
          initialAmount
          vendorSource
          transactionCurrencyCode
          ${STATE_TRANSITIONS}
          order {
            id
            uuidPrefix
            deliveryAddress
          }
          tradein {
            id
            uuidPrefix
            deliveryAddress
            countryCode
          }
        }
      }
    }
  `;

  const data = await execAdmin<FetchGiftcardsFilters, FetchGiftcardsResponse>(
    query,
    { variables: filters }
  );

  // Every field on PagedGiftcardAdmin is nullable, and a broken response otherwise renders
  // identically to a legitimately empty one: `rows: null` throws inside the data provider, and
  // `count: null` becomes a silent `total: undefined` that breaks pagination without an error.
  const page = data?.giftcards;
  if (!page || !Array.isArray(page.rows)) {
    throw new Error('Gift card search returned no result set');
  }
  // A null count is as malformed as null rows, and tolerating it is not the safe option it looks
  // like: `total: 0` makes react-admin render the rows it has while offering no page 2, so the
  // tail of the result set is silently unreachable — the same failure the always-send pageSize
  // rule exists to prevent.
  if (typeof page.count !== 'number') {
    throw new Error('Gift card search returned no result count');
  }

  // The schema makes rows a nullable list of nullable elements, so this narrowing is what lets
  // every caller treat the result as a plain array. Report what it drops: silently returning 17
  // of 20 rows under a "1–20 of 143" pager means an operator working a reconciliation list never
  // learns those records existed.
  const rows = page.rows.filter((row): row is GiftcardRecord => row != null);
  if (rows.length !== page.rows.length) {
    console.error('Gift card page dropped unreadable rows', {
      dropped: page.rows.length - rows.length,
      received: page.rows.length,
    });
  }

  return { rows, count: page.count };
};

interface SensitiveDetailsResponse {
  // Null when the id doesn't resolve for this partner — guarded below so the
  // failure reads as "not found" rather than a raw TypeError in a toast.
  giftcard: { sensitiveDetails: string | null } | null;
}

/**
 * Reveal the full number or PIN. Server-side this writes an audited "revealed"
 * state transition, which is why the reveal history exists.
 */
export const revealSensitiveDetail = async (
  giftcardId: number,
  field: 'number' | 'pin'
): Promise<string | null> => {
  const query = `
    query fetchGiftcardDetails($giftcardId: Int!, $field: String!) {
      giftcard(id: $giftcardId) {
        sensitiveDetails(field: $field)
      }
    }
  `;
  const data = await execAdmin<{ giftcardId: number, field: string }, SensitiveDetailsResponse>(
    query,
    { variables: { giftcardId, field } }
  );
  if (!data.giftcard) {
    throw new Error(`Gift card ${giftcardId} not found`);
  }
  return data.giftcard.sensitiveDetails;
};

interface FetchGiftcardConstantsResponse {
  application: {
    constants: {
      giftcard: {
        states: GiftcardConstants['states'];
        sources: GiftcardConstants['sources'];
        reasons: GiftcardConstants['reasons'];
      };
    };
  };
}

/** Gift card states, sources, and reasons for filters. */
export const fetchGiftcardConstants = async (): Promise<GiftcardConstants> => {
  const query = `
    query fetchGiftcardConstants {
      application {
        constants {
          giftcard {
            states { code displayName }
            sources { code displayName }
            reasons { code displayName }
          }
        }
      }
    }
  `;

  const data = await execAdmin<Record<string, never>, FetchGiftcardConstantsResponse>(
    query,
    { variables: {} }
  );

  // Optional chaining to match the two fetchers above. The caller treats a throw as "fall back to
  // defaults", so an unguarded three-level walk would still degrade acceptably — but it would do
  // it via a TypeError, which reads as a bug rather than a bad response.
  const giftcard = data?.application?.constants?.giftcard;
  if (!giftcard) {
    throw new Error('Gift card constants came back in an unexpected shape');
  }

  return {
    states: giftcard.states,
    sources: giftcard.sources,
    reasons: giftcard.reasons,
  };
};
